<template>
  <div>
    <v-card class="sf-card"
            flat>
      <div class="sf-card__header"
           v-if="!hideHeader">
        <h2 class="sf-card__title">{{ $t('products') }}</h2>
        <div class="sf-card__tools">
          <v-text-field class="sf-search sf-products__search"
                        dense
                        flat
                        hide-details
                        prepend-inner-icon="ti-search"
                        solo
                        v-bind:placeholder="$t('search')"
                        v-model="search" />
          <v-btn-toggle class="sf-products__periods"
                        dense
                        mandatory
                        v-model="selectedDeliveryPeriod"
                        v-if="isMobile"
                        active-class="sf-products__period--active">
            <v-btn small
                   text>
              {{ supplier.deliveryPeriod0Tag | defaultValue($t('period1')) }}
            </v-btn>
            <v-btn small
                   text
                   v-if="supplier.deliveryPeriod1Tag != null && supplier.deliveryPeriod1Tag.length !== 0">
              {{ supplier.deliveryPeriod1Tag | defaultValue($t('period2')) }}
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>
      <div class="sf-products__periods-bar"
           v-if="hideHeader && isMobile">
        <v-btn-toggle class="sf-products__periods"
                      dense
                      mandatory
                      v-model="selectedDeliveryPeriod"
                      active-class="sf-products__period--active">
          <v-btn small
                 text>
            {{ supplier.deliveryPeriod0Tag | defaultValue($t('period1')) }}
          </v-btn>
          <v-btn small
                 text
                 v-if="supplier.deliveryPeriod1Tag != null && supplier.deliveryPeriod1Tag.length !== 0">
            {{ supplier.deliveryPeriod1Tag | defaultValue($t('period2')) }}
          </v-btn>
        </v-btn-toggle>
      </div>
      <v-progress-linear color="accent"
                         indeterminate
                         v-bind:active="loading" />
      <v-form ref="form">
        <template v-for="(product, index) of filteredProducts">
          <div class="sf-product"
               v-bind:key="`${product.id}-card_text`"
               v-bind:class="isHighlighted(product) ? 'sf-product--highlighted' : null">
            <v-row align="center">
              <v-col cols="6" sm="auto" v-if="!isMobile || selectedDeliveryPeriod === 0">
                <v-tooltip right color="accent" v-bind:disabled="true">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="sf-product__period">
                      {{ supplier.deliveryPeriod0Tag | defaultValue($t('period1')) }}
                    </div>
                    <v-text-field v-bind:background-color="highlightQuantityColor(product, 0)"
                                  v-bind:dark="highlightQuantityDark(product, 0)"
                                  v-bind:rules="[ rules.validQuantity(product, 0) ]"
                                  class="quantity-field"
                                  v-bind:readonly="preview"
                                  dense
                                  flat
                                  hide-details="auto"
                                  solo
                                  suffix="x"
                                  type="number"
                                  v-model="product.orderQuantity[0]"
                                  v-on="on"
                                  v-bind="attrs" />
                  </template>
                  <span>{{ $t('theQuantityhasToBeAtLeast', { min: product.quantity[0] })}}</span>
                </v-tooltip>
              </v-col>
              <v-col cols="6" sm="auto" v-if="(!isMobile || selectedDeliveryPeriod === 1) && supplier.deliveryPeriod1Tag != null && supplier.deliveryPeriod1Tag.length !== 0">
                <v-tooltip right color="accent" v-bind:disabled="true">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="sf-product__period">
                      {{ supplier.deliveryPeriod1Tag | defaultValue($t('period2')) }}
                    </div>
                    <v-text-field v-bind:background-color="highlightQuantityColor(product, 1)"
                                  v-bind:dark="highlightQuantityDark(product, 1)"
                                  v-bind:rules="[ rules.validQuantity(product, 1) ]"
                                  class="quantity-field"
                                  v-bind:readonly="preview"
                                  dense
                                  flat
                                  hide-details="auto"
                                  solo
                                  suffix="x"
                                  type="number"
                                  v-model="product.orderQuantity[1]"
                                  v-on="on"
                                  v-bind="attrs" />
                  </template>
                  <span>{{ $t('theQuantityhasToBeAtLeast', { min: product.quantity[1] })}}</span>
                </v-tooltip>
              </v-col>
              <v-col cols="auto" v-if="editableImages">
                <product-image-editor v-bind:invitation-code="invitationCode"
                                      v-bind:product="product" />
              </v-col>
              <v-col cols="auto" v-else-if="product.imageUrl != null">
                <v-menu offset-y open-on-hover>
                  <template v-slot:activator="{ on, attrs }">
                    <div class="sf-product__thumb"
                         v-bind="attrs"
                         v-on="on">
                      <v-img contain
                             height="56"
                             width="56"
                             v-bind:alt="product.title"
                             v-bind:src="product.imageUrl" />
                    </div>
                  </template>
                  <v-card class="sf-product__preview"
                          flat>
                    <v-img contain
                           eager
                           height="400"
                           width="400"
                           v-bind:alt="product.title"
                           v-bind:src="product.imageUrl" />
                  </v-card>
                </v-menu>
              </v-col>
              <v-col class="text-wrap sf-product__info"
                     cols="12"
                     sm="">
                <div class="sf-product__unit">
                  <strong>{{ product.orderUnit != null && product.orderUnit.length !== 0 ? product.orderUnit : '' }}</strong>
                  <span class="sf-muted"> {{ product.packagingText != null && product.packagingText.length !== 0 ? `(${product.packagingText})` : '' }}</span>
                </div>

                <div class="sf-product__title">
                  <strong v-if="product.label != null" v-bind:class="{ 'accent--text': product.label === 'NEU' }">{{ product.label }}:</strong>
                  {{ product.title }} <span class="sf-muted">({{ product.id }})</span>
                  <v-tooltip bottom
                            max-width="320"
                            v-if="product.newText2 != null && product.newText2.length !== 0">
                    <template v-slot:activator="{ on }">
                      <v-icon color="primary"
                              small
                              v-on="on">
                        ti-info-circle
                      </v-icon>
                    </template>
                    <span>{{ product.newText2 }}</span>
                  </v-tooltip>
                </div>

                <div class="sf-product__lead"
                     v-if="product.newText1">
                  {{ product.newText1 }}
                </div>
              </v-col>
              <v-col class="sf-product__price"
                     cols="12" sm="auto"
                     v-if="!preview">
                <strong>{{ formatPrice(product.price) }}*</strong><br>
                <!--<span v-if="getDiscount(product, 0) != null" class="accent--text">{{ getDiscount(product, 0) }}</span>-->
                <span v-if="product.discount != null" class="accent--text">{{ product.discount }}</span>
              </v-col>
            </v-row>
          </div>

          <v-divider v-bind:key="`${product.id}-divider`"
                    v-if="index !== products.length - 1" />
        </template>
      </v-form>
    </v-card>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import ProductImageEditor from '@/components/ProductImageEditor'

  export default {
    components: {
      ProductImageEditor
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.smAndDown
      },
      rules() {
        return {
          validQuantity: (p, d) => v => {
            if (v == null || v.length === 0 || v % 1 != 0) {
              return false
            }

            /*if (v < p.quantity[d]) {
              return false
            }*/

            if (v < 0) {
              return false
            }

            return true
          }
        }
      },
      filteredProducts() {
        let products = this.products

        const term = this.filterText != null ? this.filterText : this.search

        if (term != null && term.length !== 0) {
          const search = term.toLowerCase()

          products = products.filter(p => `${p.id} ${p.title}`.toLowerCase().includes(search))
        }

        return products
      },
      ...mapGetters([ 'currentCustomer' ])
    },
    data: () => ({
      selectedDeliveryPeriod: 0,
      search: null
    }),
    methods: {
      isHighlighted(product) {
        if (this.currentCustomer == null) {
          return false
        }

        if (!this.currentCustomer.branch) {
          return false
        }

        return product.commodityGroup === '300' || product.commodityGroup === '301' || product.commodityGroup === '302'
      },
      getDiscount(product, deliveryPeriod) {
        const discount = product.discounts.find(d => d.deliveryPeriod === deliveryPeriod)

        if (discount == null) {
          return null
        }

        return discount.value
      },
      highlightQuantityColor(product, deliveryPeriod) {
        const order = product.orders.find(o => o.deliveryPeriod === deliveryPeriod)

        if (product.orderQuantity[deliveryPeriod] == null || product.orderQuantity[deliveryPeriod].length === 0 || product.orderQuantity[deliveryPeriod] % 1 != 0 || product.orderQuantity[deliveryPeriod] < 0) {
          return 'accent'
        }

        if (order != null && order.quantity !== 0) {
          if (order.quantity !== product.orderQuantity[deliveryPeriod]) {
            return 'orange'
          }

          return 'light-green'
        }

        if (product.quantity[deliveryPeriod] !== product.orderQuantity[deliveryPeriod]) {
          return 'orange'
        }

        if (order != null && order.quantity !== product.orderQuantity[deliveryPeriod]) {
          return 'orange'
        }

        return 'grey lighten-3'
      },
      highlightQuantityDark(product, deliveryPeriod) {
        const order = product.orders.find(o => o.deliveryPeriod === deliveryPeriod)

        if (product.orderQuantity[deliveryPeriod] == null || product.orderQuantity[deliveryPeriod].length === 0 || product.orderQuantity[deliveryPeriod] % 1 != 0 || product.orderQuantity[deliveryPeriod] < 0) {
          return true
        }

        if (product.quantity[deliveryPeriod] !== product.orderQuantity[deliveryPeriod]) {
          return true
        }

        if (order == null) {
          return false
        }

        return order.quantity != 0 || order.quantity !== product.orderQuantity[deliveryPeriod]
      },
      formatPrice(price) {
        if (price == null || price <= 0) {
          return 'n. v.'
        }

        return price.toLocaleString(this.$i18n.locale, {
          style: 'currency',
          currency: 'EUR'
        })
      },
      validate() {
        return this.$refs.form.validate()
      }
    },
    name: 'ProductSheetCard',
    props: {
      /** Zeigt je Artikel den Bild-Editor (Design-Vorschau für Lieferant/Administrator). */
      editableImages: Boolean,
      filterText: String,
      hideHeader: Boolean,
      /** Einladungscode des Lieferanten für den Bild-Upload; ohne Code wird das Access-Token (Administrator) verwendet. */
      invitationCode: String,
      preview: Boolean,
      //deliveryPeriod: Number,
      products: Array,
      loading: {
        default: false,
        type: Boolean
      },
      supplier: Object
    }
  }
</script>

<style scoped>
  .sf-products__search {
    flex: 1 1 200px;
    max-width: 320px;
  }

  .sf-products__periods-bar {
    border-bottom: 1px solid var(--sf-border);
    padding: 12px 16px;
  }

  .sf-products__periods-bar .sf-products__periods {
    display: flex;
  }

  .sf-products__periods-bar >>> .v-btn {
    flex: 1 1 0;
  }

  .sf-products__periods {
    border: 1px solid var(--sf-border-strong);
    border-radius: 999px;
    overflow: hidden;
  }

  .sf-products__periods >>> .v-btn {
    border: 0 !important;
    border-radius: 999px !important;
    min-width: 0;
  }

  .sf-products__period--active {
    background: var(--sf-primary-soft) !important;
    color: var(--sf-primary) !important;
  }

  .sf-product {
    padding: 12px 24px;
  }

  .sf-product--highlighted {
    background: var(--sf-primary-soft);
  }

  .sf-product__period {
    color: var(--sf-text-secondary);
    font-size: .75rem;
    font-weight: 600;
    letter-spacing: .04em;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  .quantity-field {
    width: 128px;
  }

  .quantity-field >>> .v-input__slot {
    border-radius: 10px !important;
    min-height: 40px !important;
    padding: 0 12px !important;
  }

  .quantity-field >>> input {
    font-size: 1rem;
    font-weight: 600;
  }

  .sf-product__thumb {
    background: #fff;
    border: 1px solid var(--sf-border);
    border-radius: 10px;
    cursor: zoom-in;
    overflow: hidden;
    padding: 2px;
  }

  .sf-product__preview {
    background: #fff;
    line-height: 0;
  }

  .sf-product__unit {
    font-size: .875rem;
  }

  .sf-product__title {
    font-size: .9375rem;
    line-height: 1.5;
  }

  .sf-product__lead {
    color: var(--sf-text-secondary);
    font-size: .875rem;
  }

  .sf-product__price {
    font-size: 1rem;
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
  }

  @media (max-width: 599px) {
    .sf-product {
      padding: 12px 16px;
    }

    .sf-product__price {
      text-align: left;
    }

    .quantity-field {
      width: 100%;
    }
  }
</style>

<i18n>
  {
    "de": {
      "products": "Artikel",
      "theQuantityhasToBeAtLeast": "Die Menge muss mindestens {min} betragen.",
      "period1": "Zeitraum 1",
      "period2": "Zeitraum 2",
      "search": "Suchen"
    },
    "en": {
      "products": "Products",
      "theQuantityhasToBeAtLeast": "The quanitty has to be at least {min}.",
      "period1": "Period 1",
      "period2": "Period 2",
      "search": "Search"
    }
  }
</i18n>